// FinishAndPostPanel.tsx — the P1 correction checklist + the Post gate.
//
// Shows each correction station green/red with its evidence. The Post button is
// GATED (D3) on all-green — a found defect blocks posting until resolved or an
// explicit override. Per-defect actions (re-cook / send-to-production, D4) are
// stubbed as buttons that will call the repair endpoints when P1.5's re-cook lands.
//
// Reads via getPostProductionChecklistByRun. (session 8, F1) Routed by the run's tenant: a vms:covers run through the engine
// console (the operator's key), TubeForge's own through the backend; another agency's run is finished by that agency.
// Matches the admin pages' conventions: useState/useEffect, inline styles, the api client.

import { useState, useEffect, useCallback } from "react";
import {
  getPostProductionChecklistByRun,
  runPostProductionByRun,
  approvePostProductionByRun,
  getPlatformVersionsByRun,
  getLibraryCatalogue,
  type CatalogueItem,
  type CorrectionChecklist,
  type CorrectionResult,
} from "../../api/post-production.ts";
import { finishingSide } from "../../api/engine-console-rules.ts";
import {
  versionState, masterState, MASTER_NOTE, refusedWarning, versionLabel, versionKey, lengthText, coverText, checkText, wordsLines, copyVerdict, gradeView,
  VERSION_PLATFORMS, VERSIONS_NOTE, type VersionsView,
} from "../../lib/platform-versions.ts";

export default function FinishAndPostPanel({ productionRunId, tenantId, callback }: {
  productionRunId: string;
  /** (session 8, F1) the run's tenant, when known — decides who finishes it (unknown = the app, as before) */
  tenantId?: string;
  /** Phase 4b: "growth" when mounted on a growth item, so the item records its finished URL. */
  callback?: "growth";
}) {
  const [data, setData] = useState<CorrectionChecklist | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [approved, setApproved] = useState<string | null>(null);   // audit fix: the gate "Approve & Post" just decided
  // (tf3-27) the platform versions of the newest finishing run; null: none yet (404), or not read
  const [versions, setVersions] = useState<VersionsView | null>(null);
  const [versionsError, setVersionsError] = useState<string | null>(null);
  // P4.1 — the finishing/versioning params the controls build (music/brand/lang/…).
  const [params, setParams] = useState<PostProductionParams>({});
  const side = finishingSide(tenantId);
  const via = side === "engine" ? "engine" : "app";

  const load = useCallback(
    async (signal?: AbortSignal) => {
      setLoading(true);
      setError(null);
      try {
        setData(await getPostProductionChecklistByRun(productionRunId, signal, via));
        // (tf3-27) the versions: a 404 is "no finishing run yet" (nothing to show); any other failure is said, and the
        //  checklist still stands
        setVersionsError(null);
        // (audit 10-16) a load for an earlier run (aborted when the run changed) never writes this run's versions
        try { const v = await getPlatformVersionsByRun(productionRunId, via, signal); if (!signal?.aborted) setVersions(v); }
        catch (e) {
          if (signal?.aborted) return;
          setVersions(null);
          const m = (e as Error).message;
          if (!/no finishing run/i.test(m)) setVersionsError(m);
        }
      } catch (e) {
        setError((e as Error).message);
      } finally {
        setLoading(false);
      }
    },
    [productionRunId, via],
  );

  useEffect(() => {
    if (side === "agency") return;   // not ours to finish — nothing to read
    const ac = new AbortController();
    void load(ac.signal);
    return () => ac.abort();
  }, [load, side]);

  const startRun = async () => {
    setBusy(true);
    setError(null);
    try {
      await runPostProductionByRun(productionRunId, buildParams(params), callback, via);
      await load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  if (side === "agency") return <div style={{ padding: 16, color: "#666", fontSize: 13 }}>Finished by its agency.</div>;
  if (loading) return <div style={{ color: "#666", fontSize: 13 }}>Loading correction checks…</div>;
  if (error) return <div style={{ color: "#c00", fontSize: 13 }}>Error: {error}</div>;

  if (!data || data.notStarted) {
    return (
      <div style={{ padding: 16 }}>
        <div style={{ fontWeight: 600, marginBottom: 8 }}>Finish &amp; Post</div>
        <div style={{ color: "#666", fontSize: 13, marginBottom: 12 }}>
          Choose what to apply, then run. Correction checks run first; finishing &amp; versioning apply on the
          re-stitch. Leave everything blank for a plain correction pass.
        </div>
        <PostProductionControls value={params} onChange={setParams} />
        <button onClick={startRun} disabled={busy} style={{ ...btn(true), marginTop: 12 }}>
          {busy ? "Starting…" : "Start finishing run"}
        </button>
      </div>
    );
  }

  const canPost = data.allPassed && !data.pending;

  return (
    <div style={{ padding: 16 }}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>Finish &amp; Post — correction checklist</div>
      <div style={{ color: "#666", fontSize: 12, marginBottom: 12 }}>
        Every check must pass before this clone can be posted.
        {data.pending ? " Some checks are still running." : ""}
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {data.results.map((r) => (
          <ChecklistRow key={r.stationId} r={r} productionRunId={productionRunId} onChanged={() => load()} />
        ))}
      </div>

      {versions && <PlatformVersions view={versions} />}
      {versionsError && <div style={{ color: "#c00", fontSize: 12, marginTop: 12 }}>The platform versions couldn't be read: {versionsError}</div>}
      {refusedWarning(versions) && (
        <div role="alert" style={{ marginTop: 12, color: "#92400e", background: "#fffbeb", border: "1px solid #fcd34d", borderRadius: 6, padding: "8px 12px", fontSize: 12.5 }}>
          {refusedWarning(versions)}
        </div>
      )}

      <div style={{ marginTop: 16, display: "flex", alignItems: "center", gap: 12 }}>
        <button disabled={!canPost || busy} style={btn(canPost)} title={canPost ? "" : "Resolve all defects first"}
          onClick={async () => {
            // Audit fix: this button had no handler. It approves the finishing run's waiting gate — correction, then
            // delivery (one per click) — so the run can finish and the deliverable be delivered.
            setBusy(true); setError(null);
            try {
              const r = await approvePostProductionByRun(productionRunId, via);
              setApproved(r.approvedGate === "gate_delivery" ? "Delivered — the finished video is ready." : "Correction approved — delivery is next.");
              await load();
            } catch (e) { setError((e as Error).message); }
            finally { setBusy(false); }
          }}>
          Approve &amp; Post
        </button>
        {!canPost && (
          <span style={{ color: "#c00", fontSize: 12 }}>
            Blocked — {data.results.filter((r) => !r.passed).length} unresolved defect(s).
          </span>
        )}
        {approved && <span style={{ color: "#16a34a", fontSize: 12 }}>{approved}</span>}
        <button onClick={() => load()} style={{ ...btn(false), background: "#eee", color: "#333" }}>
          Refresh
        </button>
      </div>
    </div>
  );
}

/** (tf3-27; C1.6b handoff §2.1) the versions side by side, one column each: its state (refused with why), the file, its
 *  measured length and cover frame, each check as measured, and the words as they will post. */
function PlatformVersions({ view }: { view: VersionsView }) {
  if (view.versions.length === 0 && !view.master) return null;
  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>Platform versions</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
        {/* (tf3-33) the master first, beside the versions cut from it; none (null) → no column */}
        {view.master && (
          <div key="master" style={{ border: "1px solid #c7d2fe", background: "#f8faff", borderRadius: 6, padding: 10, fontSize: 12 }}>
            <div style={{ fontWeight: 600, fontSize: 13 }}>Master</div>
            <div style={{ color: view.master.status === "ready" ? "#2a7" : "#666", margin: "2px 0 6px" }}>{masterState(view.master)}</div>
            {view.master.url ? <video src={view.master.url} controls preload="metadata" style={{ width: "100%", borderRadius: 4 }} />
              : <div style={{ color: "#999" }}>no file</div>}
            {view.master.url && <a href={view.master.url} download style={{ fontSize: 11 }}>Download</a>}
            <div style={{ marginTop: 6, color: "#666" }}>{MASTER_NOTE}</div>
          </div>
        )}
        {view.versions.map((v) => {
          const st = versionState(v);
          return (
            <div key={versionKey(v)} style={{ border: `1px solid ${st.refused ? "#f0c6c6" : "#e5e7eb"}`, background: st.refused ? "#fdf5f5" : "#fff", borderRadius: 6, padding: 10, fontSize: 12 }}>
              <div style={{ fontWeight: 600, fontSize: 13 }}>{versionLabel(v)}</div>
              <div style={{ color: st.refused ? "#c33" : v.status === "ready" ? "#2a7" : "#666", margin: "2px 0 6px" }}>{st.text}</div>
              {v.url ? <video src={v.url} controls preload="metadata" style={{ width: "100%", borderRadius: 4 }} />
                : <div style={{ color: "#999" }}>no file</div>}
              {v.url && <a href={v.url} download style={{ fontSize: 11 }}>Download</a>}
              <div style={{ marginTop: 6 }}>Length: {lengthText(v.durationSec)}</div>
              <div>Cover: {coverText(v.cover)}</div>
              {v.checks.length > 0 && (
                <ul style={{ margin: "6px 0 0", paddingLeft: 16 }}>
                  {v.checks.map((c) => <li key={c.checkId} style={{ color: c.passed ? "#374151" : "#c33" }}>{c.passed ? "✓" : "✗"} {checkText(c)}</li>)}
                </ul>
              )}
              {wordsLines(v).map((w) => (
                <div key={w.label} style={{ marginTop: 6 }}><span style={{ color: "#666" }}>{w.label}: </span><span style={{ whiteSpace: "pre-wrap" }}>{w.text}</span></div>
              ))}
              {/* (tf3-29) publish_verify's verdict on the copy, and the platform grade (recorded only) */}
              {(() => { const c = copyVerdict(v.words); return c && <div style={{ marginTop: 6, color: c.refused ? "#c33" : "#374151" }}>{c.text}</div>; })()}
              {(() => { const g = gradeView(v.grade); return g && (
                <div style={{ marginTop: 6 }}>
                  {g.lines.map((l) => <div key={l.label}>{l.label}: <strong>{l.score}</strong> <span style={{ color: "#666" }}>— {l.reason}</span></div>)}
                  <div style={{ color: "#9ca3af", fontSize: 10.5 }}>{g.note}</div>
                </div>); })()}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ChecklistRow({
  r,
  productionRunId,
  onChanged,
}: {
  r: CorrectionResult;
  productionRunId: string;
  onChanged: () => void;
}) {
  void productionRunId;
  void onChanged;
  const ok = r.passed;
  return (
    <div
      style={{
        border: `1px solid ${ok ? "#cde8cd" : "#f0c6c6"}`,
        background: ok ? "#f4fbf4" : "#fdf5f5",
        borderRadius: 6,
        padding: "8px 12px",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ color: ok ? "#2a7" : "#c33", fontWeight: 700 }}>{ok ? "✓" : "✗"}</span>
        <span style={{ fontWeight: 600, fontSize: 13 }}>{label(r.stationId)}</span>
      </div>
      {!ok && r.defect && (
        <div style={{ color: "#933", fontSize: 12, marginTop: 4 }}>{r.defect}</div>
      )}
      {!ok && r.evidence && (
        <div style={{ color: "#666", fontSize: 11, marginTop: 2, fontFamily: "monospace" }}>{r.evidence}</div>
      )}
      {!ok && (
        <div style={{ marginTop: 6, display: "flex", gap: 8 }}>
          {/* D4 repair actions — wired when P1.5's re-cook lands. */}
          <button style={smallBtn} disabled title="Re-cook the affected beat (P1.5)">
            Re-cook beat
          </button>
          <button style={smallBtn} disabled title="Send back to production (deferred)">
            Send to production
          </button>
        </div>
      )}
    </div>
  );
}

const LABELS: Record<string, string> = {
  cover_output_check: "Cover output (no generative path on a cover beat)",
  card_over_frame_check: "Card over frame (card composited on the frame, not brand colour)",
  verify_sanity_check: "Verify sanity (the quality gate is not inverted)",
  artefact_exists_check: "Artefact exists (succeeded steps have their output)",
  motion_source_check: "Motion source (eligible beats have a motion source)",
};
const label = (id: string) => LABELS[id] ?? id;

const btn = (enabled: boolean): React.CSSProperties => ({
  padding: "8px 16px",
  borderRadius: 6,
  border: "none",
  background: enabled ? "#2a7" : "#bbb",
  color: "#fff",
  fontWeight: 600,
  fontSize: 13,
  cursor: enabled ? "pointer" : "not-allowed",
});
const smallBtn: React.CSSProperties = {
  padding: "4px 10px",
  borderRadius: 5,
  border: "1px solid #ccc",
  background: "#fff",
  fontSize: 12,
  cursor: "not-allowed",
  color: "#999",
};

// ── P4.1 — the finishing/versioning param controls ───────────────────────────
// The operator's choices → the `params` the post_production run reads (ctx.target).
// Enumerable inputs (language, platform, normalize, brand-native) are real controls;
// music/brand REFS are id inputs for v1 (a music-library / brand-asset PICKER needs
// list endpoints — a follow-up; noted in the P4.1 README).

interface PostProductionParams {
  lang?: string;
  platform?: string;
  normalize?: boolean;
  musicRefId?: string;      // → params.musicRef { id, type:"music_style" }
  duckDb?: number;
  brandLogoAssetId?: string; // → params.brand { logoAssetId }
  brandNative?: boolean;     // → params.brand { native: true }
  captionsOff?: boolean;     // → params.captions { off: true } — turn karaoke captions off
}

// Common target languages (BCP-47), from the ElevenLabs Dubbing v2 supported set.
// The API accepts 90+; this is a curated dropdown of high-value markets + a few
// dialects. Extend from the doc's table as needed (the codes are the real tags).
const LANGUAGES: Array<{ code: string; label: string }> = [
  { code: "", label: "— none (no translation) —" },
  { code: "es", label: "Spanish" },
  { code: "es-MX", label: "Spanish (Mexico)" },
  { code: "es-ES", label: "Spanish (Spain)" },
  { code: "pt-BR", label: "Portuguese (Brazil)" },
  { code: "fr", label: "French" },
  { code: "fr-CA", label: "French (Canada)" },
  { code: "de", label: "German" },
  { code: "it", label: "Italian" },
  { code: "ja", label: "Japanese" },
  { code: "ko", label: "Korean" },
  { code: "zh", label: "Chinese (Mandarin)" },
  { code: "hi", label: "Hindi" },
  { code: "ar", label: "Arabic" },
  { code: "id", label: "Indonesian" },
  { code: "vi", label: "Vietnamese" },
  { code: "tr", label: "Turkish" },
];

// (tf3-27) the platform choice is which version to make (lib/platform-versions.ts); no long-form YouTube version exists
const PLATFORMS = VERSION_PLATFORMS;

/** Flatten the control state into the `params` the backend/ingest/stations read. */
function buildParams(p: PostProductionParams): Record<string, unknown> | undefined {
  const out: Record<string, unknown> = {};
  if (p.lang) out.lang = p.lang;
  if (p.platform) out.platform = p.platform;
  if (p.normalize) out.normalize = true;
  if (p.musicRefId) out.musicRef = { id: p.musicRefId, type: "music_style" };
  if (typeof p.duckDb === "number") out.duckDb = p.duckDb;
  if (p.brandNative) out.brand = { native: true };
  else if (p.brandLogoAssetId) out.brand = { logoAssetId: p.brandLogoAssetId };
  if (p.captionsOff) out.captions = { off: true };
  return Object.keys(out).length ? out : undefined;
}

function PostProductionControls({
  value,
  onChange,
}: {
  value: PostProductionParams;
  onChange: (p: PostProductionParams) => void;
}) {
  const set = (patch: Partial<PostProductionParams>) => onChange({ ...value, ...patch });
  // P4.2 — load the music catalogue for the picker (best-effort; falls back to a
  // plain id input if the library proxy isn't configured / unreachable).
  const [musicItems, setMusicItems] = useState<CatalogueItem[] | null>(null);
  const [catErr, setCatErr] = useState(false);
  useEffect(() => {
    const ac = new AbortController();
    getLibraryCatalogue(["music_style"], ac.signal)
      .then((r) => setMusicItems(r.items ?? []))
      .catch(() => setCatErr(true));  // catch-reason: the error is shown to the person (setCatErr: the catalogue is unreachable)
    return () => ac.abort();
  }, []);
  const row: React.CSSProperties = { display: "flex", alignItems: "center", gap: 8, marginBottom: 8, fontSize: 13 };
  const lbl: React.CSSProperties = { width: 120, color: "#374151" };
  const inp: React.CSSProperties = { flex: 1, padding: "4px 8px", border: "1px solid #d1d5db", borderRadius: 5, fontSize: 13 };

  return (
    <div style={{ border: "1px solid #e5e7eb", borderRadius: 8, padding: 12, background: "#fafafa" }}>
      {/* AUDIO */}
      <div style={row}>
        <span style={lbl}>Music bed</span>
        {musicItems && musicItems.length > 0 ? (
          <select style={inp} value={value.musicRefId ?? ""} onChange={(e) => set({ musicRefId: e.target.value || undefined })}>
            <option value="">— none —</option>
            {musicItems.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
        ) : (
          <input
            style={inp}
            placeholder={catErr ? "music_style id (catalogue unavailable)" : "music_style id (optional)"}
            value={value.musicRefId ?? ""}
            onChange={(e) => set({ musicRefId: e.target.value || undefined })}
          />
        )}
      </div>
      <div style={row}>
        <span style={lbl}>Duck (dB)</span>
        <input
          style={{ ...inp, flex: "0 0 80px" }}
          type="number" min={-40} max={0}
          placeholder="-16"
          value={value.duckDb ?? ""}
          onChange={(e) => set({ duckDb: e.target.value === "" ? undefined : Number(e.target.value) })}
        />
        <label style={{ display: "flex", alignItems: "center", gap: 6, color: "#374151" }}>
          <input type="checkbox" checked={!!value.normalize} onChange={(e) => set({ normalize: e.target.checked })} />
          loudness-normalize the deliverable
        </label>
      </div>
      {/* CAPTIONS */}
      <div style={row}>
        <span style={lbl}>Captions</span>
        <label style={{ display: "flex", alignItems: "center", gap: 6, color: "#374151" }}>
          <input type="checkbox" checked={!!value.captionsOff} onChange={(e) => set({ captionsOff: e.target.checked })} />
          turn OFF karaoke captions (removes the auto word-by-word captions)
        </label>
      </div>
      {/* BRANDING */}
      <div style={row}>
        <span style={lbl}>Branding</span>
        <label style={{ display: "flex", alignItems: "center", gap: 6, color: "#374151" }}>
          <input
            type="checkbox"
            checked={!!value.brandNative}
            onChange={(e) => set({ brandNative: e.target.checked, brandLogoAssetId: e.target.checked ? undefined : value.brandLogoAssetId })}
          />
          native (no logo — cross-post camouflage)
        </label>
      </div>
      {!value.brandNative && (
        <div style={row}>
          <span style={lbl}>Logo asset</span>
          <input
            style={inp}
            placeholder="brand logo asset id (optional)"
            value={value.brandLogoAssetId ?? ""}
            onChange={(e) => set({ brandLogoAssetId: e.target.value || undefined })}
          />
        </div>
      )}
      {/* VERSIONING */}
      <div style={row}>
        <span style={lbl}>Language</span>
        <select style={inp} value={value.lang ?? ""} onChange={(e) => set({ lang: e.target.value || undefined })}>
          {LANGUAGES.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
        </select>
      </div>
      <div style={row}>
        <span style={lbl}>Platform</span>
        <select style={inp} value={value.platform ?? ""} onChange={(e) => set({ platform: e.target.value || undefined })}>
          {PLATFORMS.map((pl) => <option key={pl.code} value={pl.code}>{pl.label}</option>)}
        </select>
      </div>
      <div style={{ color: "#9ca3af", fontSize: 11.5, marginTop: 4 }}>
        {VERSIONS_NOTE}
      </div>
    </div>
  );
}
